"use client";

import Image from "next/image";

import type { ShopperBasketsTypes } from "commerce-sdk-isomorphic";

import { SalesPrice } from "@/components/salesPrice/salesPrice.component";
import { Text } from "@/components/text/text.component";

import {
  buildEcommerceEvent,
  createEcommercePayload,
  mapProductToItem,
} from "@/lib/analytics/ecommerce-analytics";

import { updateCartBasketItem } from "@/app/blu/(checkout-flow)/actions";
import { EVENTS } from "@/constants/analytics.constants";
import { useTagging } from "@/hooks/useTagging";
import { GetCartLineProductsQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { QuantityStepper } from "../quantityStepper/quantityStepper.component";

export const CartAccessoryLineItem = ({
  product,
  item,
  action,
  isBonusLineItem,
}: CartAccessoryLineItemProps) => {
  const [, setTag] = useTagging();

  const fireRemoveFromCart = () => {
    const itemPrice = item.salesPrice ?? item.price ?? 0;
    const items = [
      mapProductToItem(
        {
          id: item.productId,
          name: item.productName,
          salesPrice: itemPrice,
          type: (item as { c_type?: string }).c_type,
        },
        { index: 0, add_on: "n" },
      ),
    ];
    const payload = createEcommercePayload(items, {
      value: itemPrice,
    });
    setTag(buildEcommerceEvent(EVENTS.REMOVE_FROM_CART, payload), {
      reset: true,
    });
  };

  const image = product?.featuredImage?.image
    ? urlFor(product?.featuredImage?.image).width(120).height(120).url()
    : null;
  const renderBundledItems = (
    bundleItems?: { quantity?: number; productName?: string }[],
  ) => {
    if (!bundleItems?.length) return null;
    return bundleItems.map((bp, i: number) => (
      <li
        data-testid="accessory-line-item-bundled-product"
        key={i}
        className="flex items-center gap-2">
        <Text key={i} variant="body4" className="text-greyscale-body">
          {bp.quantity ?? 1} {bp.productName ?? "Item"}
        </Text>
      </li>
    ));
  };
  return (
    <div
      data-testid="cart-accessory-line-item"
      data-product-id={item.productId}
      className="flex gap-4 border-b border-b-[#80808033] py-6">
      <div className="shrink-0">
        {image ? (
          <Image
            src={image}
            alt={product?.featuredImage?.alt ?? ""}
            width={80}
            height={80}
            className="h-16 w-16 rounded-lg object-cover md:h-20 md:w-20"
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <Text variant="body2">{product?.name || item.productName}</Text>
        <ul
          data-testid="accessory-line-item-bundled-products"
          className="flex flex-col gap-0.5">
          {renderBundledItems(
            product?.bundledProducts || item.optionProductLineItems,
          )}
        </ul>
        <div className="flex items-center justify-between">
          {!isBonusLineItem ? (
            <QuantityStepper
              id={item.itemId ?? item.UUID}
              quantity={item.quantity}
              formAction={action ?? updateCartBasketItem}
              onRemove={fireRemoveFromCart}
            />
          ) : null}
          <div className="w-full md:hidden">
            <SalesPrice
              salesPrice={item.salesPrice}
              listPrice={item.listPrice}
              free={item.salesPrice === 0 || isBonusLineItem}
              quantity={item.quantity}
              salesPriceClassName="text-[16px] font-bold"
              reverse
            />
          </div>
        </div>
      </div>
      <div className="hidden shrink-0 items-start pt-0.5 md:flex">
        <SalesPrice
          listPrice={item.listPrice}
          salesPrice={item.salesPrice}
          quantity={item.quantity}
          free={item.priceFree || isBonusLineItem}
          monthly={item.priceUnit === "monthly"}
          salesPriceClassName="lg:text-[16px] font-bold"
          reverse
        />
      </div>
    </div>
  );
};

type CartAccessoryLineItemProps = {
  product?: GetCartLineProductsQueryResult[number];
  item: ShopperBasketsTypes.ProductItem;
  isBonusLineItem?: boolean;
  action?: (
    prevState: ResponseMessage,
    formData: FormData,
  ) => ResponseMessage | Promise<ResponseMessage>;
};
